import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  IconButton,
} from "@mui/material";
import { useUser } from "../../pages/login/UserContextPersistent";
import DashboardMap from "./DashboardMap";
import MapSkeleton from "./MapSkeleton";
import LiveIcon from "../../images/dashboard/map_live.svg";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import "./Dashboard.css";
import { useTranslation } from "react-i18next";
import {
  getAllStops,
  getAllRoutes,
  getAllVehicles,
  handleApiError,
} from "./dashboardAPI";
import { useNavigate } from "react-router";
import { useUserInfo } from "../../hooks/useUserInfo";
import PerformanceAnalytics from "./PerformanceAnalytics";

const getVehiclesForRoutes = (allVehicles = [], routes = []) => {
  const routeIds = new Set(
    routes.map((route) => route?.id?.toString()).filter(Boolean),
  );
  const routeVehicleIds = new Set(
    routes.flatMap((route) =>
      (route?.vehicles || [])
        .map((vehicle) => vehicle?.device_id?.toString())
        .filter(Boolean),
    ),
  );

  return allVehicles.filter((vehicle) => {
    const deviceId = vehicle?.device_id?.toString();
    const routeId = vehicle?.routeId?.toString();

    return (
      (routeId && routeIds.has(routeId)) ||
      (deviceId && routeVehicleIds.has(deviceId))
    );
  });
};

export default function DashboardMapFilter({
  filterBy = "routes",
  dashboard,
  compact = false,
  showLiveIndicator = true,
  headerTitle,
  showExpandButton = false,
  emptyLabel,
  showStops = true,
  showVehicles = true,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, organizationInactive } = useUser();
  const hideLiveDashboardSections = Boolean(dashboard && organizationInactive);

  const { orgId } = useUserInfo();

  const [isLoading, setIsLoading] = useState(true);
  // const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [data, setData] = useState({
    vehicles: [],
    routes: [],
    stops: [],
    users: [],
  });

  // Filtered data state
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedItems(selectedValues);
    filterDataByItems(selectedValues);
  };

  const filterDataByItems = (selectedItemIds) => {
    if (selectedItemIds.length === 0) {
      // If no items selected, show all data
      setFilteredRoutes(data.routes);
      setFilteredStops(data.stops);
      setFilteredVehicles(data.vehicles);
      return;
    }

    if (filterBy === "routes") {
      // Existing route filtering logic
      const routes = data.routes.filter((route) =>
        selectedItemIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      const stops = data.stops.filter((stop) => {
        return selectedItemIds.some((routeId) => {
          const selectedRouteObj = data.routes.find(
            (r) => r.id.toString() === routeId
          );
          return selectedRouteObj?.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          );
        });
      });
      setFilteredStops(stops);

      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "stops") {
      // Filter by stops
      const stops = data.stops.filter((stop) =>
        selectedItemIds.includes(stop.id.toString())
      );
      setFilteredStops(stops);

      // Find routes that contain these stops
      const routes = data.routes.filter((route) => {
        return route.routeCoordinates?.some((coord) =>
          selectedItemIds.some((stopId) => {
            const selectedStop = data.stops.find(
              (s) => s.id.toString() === stopId
            );
            return (
              selectedStop &&
              coord.latitude === selectedStop.latitude &&
              coord.longitude === selectedStop.longitude
            );
          })
        );
      });
      setFilteredRoutes(routes);

      // Find vehicles on routes that contain these stops
      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "vehicles") {
      // Filter by vehicles
      const vehicles = data.vehicles.filter((vehicle) =>
        selectedItemIds.includes(
          vehicle.id?.toString() || vehicle.device_id?.toString()
        )
      );

      setFilteredVehicles(vehicles);

      // Find routes for these vehicles
      const routeIds = vehicles
        .map((v) => v.routeId?.toString())
        .filter(Boolean);
      const routes = data.routes.filter((route) =>
        routeIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      // Find stops on these routes
      const stops = data.stops.filter((stop) => {
        return routes.some((route) =>
          route.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          )
        );
      });
      setFilteredStops(stops);
    }
  };

  const getCurrentDataArray = () => {
    switch (filterBy) {
      case "stops":
        return data.stops;
      case "vehicles":
        return data.vehicles;
      case "routes":
      default:
        return data.routes;
    }
  };

  const getItemDisplayName = (item) => {
    switch (filterBy) {
      case "stops":
        return item.name || `Stop ${item.id}`;
      case "vehicles":
        return item.name || `Vehicle ${item.id || item.device_id}`;
      case "routes":
      default:
        return item.name || `Route ${item.id}`;
    }
  };

  const getItemId = (item) => {
    switch (filterBy) {
      case "vehicles":
        return (item.id || item.device_id)?.toString();
      default:
        return item.id?.toString();
    }
  };

  // Helper function to get placeholder text
  const getPlaceholderText = () => {
    if (emptyLabel) return t(emptyLabel);

    switch (filterBy) {
      case "stops":
        return t("Select Stops");
      case "vehicles":
        return t("Select Vehicles");
      case "routes":
      default:
        return t("Select Routes");
    }
  };

  // Helper function to get selected count text
  const getSelectedCountText = (count) => {
    switch (filterBy) {
      case "stops":
        return t("{{count}} stops selected", { count });
      case "vehicles":
        return t("{{count}} vehicles selected", { count });
      case "routes":
      default:
        return t("{{count}} routes selected", { count });
    }
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [vehicles, routes, stops] = await Promise.all([
        getAllVehicles(),
        getAllRoutes(),
        getAllStops(),
      ]);

      const initialData = {
        vehicles,
        routes,
        stops,
        users: [], // If you need users data, add it here
      };

      setData(initialData);

      // Initialize filtered data with all data
      setFilteredVehicles(vehicles);
      setFilteredRoutes(routes);
      setFilteredStops(stops);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hideLiveDashboardSections) return;
    fetchInitialData();
  }, [hideLiveDashboardSections]);

  const handleViewFull = () => {
    navigate(`/independent/dashboard?org=${orgId}`);
  };

  if (hideLiveDashboardSections) {
    return null;
  }

  return (
    <Box
      className={`dashboard-map-filter-shell${compact ? " dashboard-map-filter-shell--compact" : ""}${headerTitle ? " dashboard-map-filter-shell--card" : ""}`}
      sx={{ position: "relative", height: "100%" }}
    >
      {/* Header container with Live indicator and Select box */}
      <Box
        className="dashboard-map-filter-controls"
        sx={{
          position: headerTitle ? "relative" : "absolute",
          top: headerTitle ? "auto" : compact ? 10 : { xs: "auto", sm: 50 },
          right: headerTitle ? "auto" : compact ? 10 : { xs: 12, sm: 50 },
          bottom: headerTitle ? "auto" : compact ? "auto" : { xs: 42, sm: "auto" },
          left: headerTitle ? "auto" : compact ? "auto" : { xs: "auto", sm: 50 },
          zIndex: 5,
          display: "flex",
          justifyContent: compact ? "flex-end" : "space-between",
          alignItems: compact ? "flex-start" : { xs: "flex-end", sm: "center" },
          flexDirection: compact ? "row" : { xs: "column", sm: "row" },
          gap: compact ? 0 : { xs: 0.75, sm: 2 },
          maxWidth: compact ? "calc(100% - 20px)" : { xs: "calc(100% - 24px)", sm: "none" },
          pointerEvents: "none",
          ...(headerTitle
            ? {
                inset: "auto",
                maxWidth: "none",
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderBottom: "1px solid #e5e7eb",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#ffffff",
              }
            : {}),
        }}
      >
        {/* Live Map indicator */}
        {headerTitle ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              flex: "0 0 auto",
              pointerEvents: "auto",
            }}
          >
            <span className="dashboard-map-card-title">{t(headerTitle)}</span>
            {showLiveIndicator && (
              <span className="dashboard-map-card-live">
                <i />
                {t("LIVE")}
              </span>
            )}
          </Box>
        ) : showLiveIndicator && (
          <Box
            sx={{
              backgroundColor: "white",
              padding: { xs: "6px 10px", sm: "8px 16px" },
              borderRadius: { xs: "8px", sm: 1 },
              boxShadow: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "fit-content", sm: "auto" },
              boxSizing: "border-box",
              pointerEvents: "auto",
            }}
          >
            <img
              src={LiveIcon}
              alt="Live Map"
              style={{
                width: 20,
                height: 20,
                animation: "pulse 1.5s infinite",
              }}
            />
            <span style={{ fontSize: "12px" }}>{t("liveMap")}</span>
          </Box>
        )}

        {/* Select Box */}
        <Box
          className="dashboard-map-filter-actions"
          sx={{
            borderRadius: 1,
            display: { xs: "grid", sm: "flex" },
            gridTemplateColumns: { xs: "auto 158px", sm: "none" },
            alignItems: "center",
            justifyContent: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 0.75, sm: 0 },
            overflow: "visible",
            width: { xs: "fit-content", sm: "auto" },
            maxWidth: { xs: "100%", sm: "none" },
            backgroundColor: { xs: "rgba(255,255,255,0.95)", sm: "transparent" },
            boxShadow: { xs: "0 2px 8px rgba(0,0,0,0.25)", sm: "none" },
            padding: { xs: "4px", sm: 0 },
            pointerEvents: "auto",
            ...(headerTitle
              ? {
                  display: "flex",
                  gap: "8px",
                  marginLeft: "auto",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  padding: 0,
                }
              : {}),
          }}
        >
          {dashboard && (
            <Button
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "12px", sm: "16px" },
                color: "#FFFFFF",
                padding: { xs: "0px 8px", sm: "0px 16px" },
                marginRight: { xs: 0, sm: "8px" },
                minWidth: { xs: "70px", sm: "80px" },
                height: { xs: "34px", sm: "40px" },
                borderRadius: { xs: "6px", sm: "8px" },
                textTransform: "none",
                whiteSpace: "nowrap",
                backgroundColor: "#20A8E0",
                borderRight: "1px solid #e0e0e0",
                "&:hover": {
                  backgroundColor: "#20A8E0",
                },
              }}
              onClick={handleViewFull}
            >
              {t("Site View")}
            </Button>
          )}
          <FormControl
            size="small"
            sx={{
              width: headerTitle ? 138 : compact ? 150 : { xs: 158, sm: 200 },
              minWidth: 0,
            }}
          >
            <Select
              multiple
              displayEmpty
              value={selectedItems}
              onChange={handleItemChange}
              sx={{
                backgroundColor: "white",
                height: headerTitle ? "28px" : { xs: "34px", sm: "40px" },
                borderRadius: headerTitle ? "4px" : { xs: "6px", sm: "8px" },
                boxShadow: "none",
                minWidth: 0,
                border: headerTitle ? "1px solid #dbe3ef" : { xs: "1px solid #d1d5db", sm: "none" },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-select": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: headerTitle ? "11px" : { xs: "12px", sm: "14px" },
                  padding: headerTitle ? "6px 26px 6px 10px" : { xs: "8px 28px 8px 10px", sm: undefined },
                },
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>{getPlaceholderText()}</em>;
                }
                return getSelectedCountText(selected.length);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    overflowY: "auto",
                    borderRadius: "8px",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                      backgroundColor: " #B7BFCC",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: " #647490",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  },
                },
              }}
            >
              <MenuItem disabled value="">
                <em>{getPlaceholderText()}</em>
              </MenuItem>
              {getCurrentDataArray()?.map((item) => (
                <MenuItem
                  key={getItemId(item)}
                  value={getItemId(item)}
                  sx={{
                    fontSize: "10px",
                    padding: "6px 8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={selectedItems.includes(getItemId(item))}
                    size="small"
                    sx={{ padding: "4px" }}
                  />
                  <ListItemText
                    primary={getItemDisplayName(item)}
                    sx={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showExpandButton && (
            <IconButton
              size="small"
              onClick={handleViewFull}
              aria-label={t("View full map")}
              className="dashboard-map-expand-button"
            >
              <FullscreenIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Map Container */}
      <Box
        className="dashboard-map-filter-map"
        sx={{
          height: headerTitle ? "auto" : "100%",
          flex: headerTitle ? 1 : undefined,
          minHeight: headerTitle ? 0 : undefined,
          padding: "0px",
        }}
      >
        {isLoading ? (
          <MapSkeleton />
        ) : (
          <DashboardMap
            stops={showStops ? filteredStops : []}
            routes={filteredRoutes}
            vehicles={showVehicles ? filteredVehicles : []}
            routesOnly={!showStops && !showVehicles}
          />
        )}
      </Box>

      {dashboard && !organizationInactive && state?.features?.some(feature => feature.api_path === '/api/managedata/analytics') && (
        <Box sx={{ mt: 4 }}>
          <PerformanceAnalytics vehiclesData={filteredVehicles} />
        </Box>
      )}
    </Box>
  );
}
